import type { AuthUser } from 'aws-amplify/auth';
import { Purchases } from '@revenuecat/purchases-js';
import type { EntitlementInfos } from '@revenuecat/purchases-js';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import { LocalStorageUtils } from '../localstorage/LocalStorageUtils';
import { RCConstants } from './RCConstants';

const dataClient = generateClient<Schema>();

let isRevenuecatConfigured = false;
let purchasesInstance: Purchases | null = null;

/**
 * Forces reconfiguration of the RevenueCat Purchases instance for a new user (e.g. on login/logout).
 */
export function updatePurchasesUser(user: AuthUser | null | undefined) {
  isRevenuecatConfigured = false;
  purchasesInstance = null;
  ensureRevenuecatConfigured(user);
}

/**
 * Returns current user's RevenueCat entitlements using the web SDK.
 * Uses cached data when fresh; RevenueCat refreshes if cache is older than ~5 minutes.
 */
export async function getCurrentUserRevenuecatEntitlements(
  user: AuthUser | null | undefined
): Promise<EntitlementInfos | null> {
  try {
    const purchases = getPurchasesInstance(user);
    const customerInfo = await purchases.getCustomerInfo();
    return customerInfo?.entitlements;
  } catch (error) {
    console.error('[RCUserUtils] getCurrentUserRevenuecatEntitlements error:', error);
    return null;
  }
}

/**
 * Returns the RevenueCat app user id. Single source of truth for app user id in RC utils.
 * When user is logged in returns Cognito user id; when not returns anonymous id (from localStorage or generated and stored).
 */
export function getAppUserId(user: AuthUser | null | undefined): string {
  if (user?.userId) {
    return user.userId;
  }
  const key = LocalStorageUtils.Keys.REVENUECAT_ANONYMOUS_USER_ID;
  const stored = LocalStorageUtils.get(key);
  if (stored && stored.trim()) {
    return stored.trim();
  }
  const anonymousId = Purchases.generateRevenueCatAnonymousAppUserId();
  LocalStorageUtils.set(key, anonymousId);
  return anonymousId;
}

/**
 * Syncs logged-in user details (nickname, email) to RevenueCat via backend.
 * Call on successful login so RevenueCat has basic user info.
 * No-op for anonymous users (only runs when user is authenticated).
 */
export async function syncRevenueCatUserDetails(
  user: AuthUser | null | undefined,
  options: { nickname?: string | null; email?: string | null }
): Promise<{ success: boolean }> {
  console.info('[RCUserUtils] syncRevenueCatUserDetails user:', user);
  if (!user?.userId) {
    console.error('[RCUserUtils] syncRevenueCatUserDetails error: user is not authenticated');
    return { success: false };
  }
  const appUserId = getAppUserId(user);
  try {
    const res = await dataClient.queries.syncRevenueCatUserDetails(
      {
        appUserId,
        nickname: options.nickname ?? undefined,
        email: options.email ?? undefined,
      },
      { authMode: 'userPool' }
    );
    console.info('[RCUserUtils] syncRevenueCatUserDetails res:', res);
    const raw = res?.data;
    if (raw == null || typeof raw !== 'string') return { success: false };
    const parsed = JSON.parse(raw) as { success?: boolean };
    return { success: parsed.success === true };
  } catch (error) {
    console.error('[RCUserUtils] syncRevenueCatUserDetails error:', error);
    return { success: false };
  }

}

function ensureRevenuecatConfigured(user: AuthUser | null | undefined) {
  if (isRevenuecatConfigured && purchasesInstance) {
    // Already configured
    return;
  } 
  const apiKey = getRevenuecatPublicKey();
  if (!apiKey) {
    console.error('[RCUserUtils] ensureRevenuecatConfigured missing public API key');
    return;
  }
  const appUserId = getAppUserId(user);
  Purchases.configure({
    apiKey,
    appUserId,
  });
  purchasesInstance = Purchases.getSharedInstance();
  isRevenuecatConfigured = true;
}

/**
 * Returns the RevenueCat Purchases shared instance, configuring the SDK with the given user if needed.
 */
function getPurchasesInstance(user: AuthUser | null | undefined) {
  ensureRevenuecatConfigured(user);
  return purchasesInstance!;
}

/** Returns the RevenueCat public API key from env (Vite). */
function getRevenuecatPublicKey(): string {
  const env = (import.meta as unknown as { env: Record<string, unknown> }).env;
  return (env[RCConstants.keys.REVENUECAT_PUBLIC_API_KEY] as string) ?? '';
}

/** Returns the RevenueCat project ID from env (Vite). */
function getRevenuecatProjectId(): string {
  const env = (import.meta as unknown as { env: Record<string, unknown> }).env;
  return (env[RCConstants.keys.REVENUECAT_PROJECT_ID] as string) ?? '';
}